var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var ACCENT = "#3987e5";
/**
 * Arrows are annotation laid over the picture, not part of the interface being
 * drawn, so they get their own colour rather than the player's accent. Red also
 * carries furthest against the dark frame. It is the same red the panel uses
 * elsewhere, to avoid inventing a fifth hue.
 */
var ARROW_COLOUR = "#d95926";
var BAR = "#2b2b2b";
var FRAME = "#141414";
var CONTROL = "#9a9a9a";
/** Centre x of each right-hand control, in the order the player lays them out. */
var AUDIO_X = 214;
var SUBS_X = 238;
var PLAIN_CONTROLS = [262, 286, 306];
function menuFor(target) {
    if (target === "audioMenu")
        return [{ text: "Italiano" }, { text: "English", active: true }];
    if (target === "subtitlesMenu")
        return [{ text: "Off" }, { text: "English", active: true }];
    return null;
}
/** Straight arrow with the head worked out from the direction, so it always lines up. */
var Arrow = function (_a) {
    var from = _a.from, to = _a.to;
    var x1 = from[0], y1 = from[1];
    var x2 = to[0], y2 = to[1];
    var length = Math.hypot(x2 - x1, y2 - y1) || 1;
    var ux = (x2 - x1) / length;
    var uy = (y2 - y1) / length;
    var head = 7;
    var baseX = x2 - ux * head;
    var baseY = y2 - uy * head;
    var wingX = -uy * (head * 0.45);
    var wingY = ux * (head * 0.45);
    return (_jsxs("g", { children: [_jsx("line", { x1: x1, y1: y1, x2: baseX, y2: baseY, stroke: ARROW_COLOUR, strokeWidth: "2.2", strokeLinecap: "round" }), _jsx("polygon", { points: "".concat(x2, ",").concat(y2, " ").concat(baseX + wingX, ",").concat(baseY + wingY, " ").concat(baseX - wingX, ",").concat(baseY - wingY), fill: ARROW_COLOUR })] }));
};
/** Vertical centre line the control-bar glyphs sit on. */
var GLYPH_Y = 165;
/** The headphones glyph Video.js puts on the audio-track button. */
var Headphones = function (_a) {
    var x = _a.x, colour = _a.colour;
    return (_jsxs("g", __assign({ stroke: colour, strokeWidth: "1.3", fill: "none" }, { children: [_jsx("path", { d: "M ".concat(x - 6, " ").concat(GLYPH_Y, " A 6 6 0 0 1 ").concat(x + 6, " ").concat(GLYPH_Y) }), _jsx("rect", { x: x - 7.5, y: GLYPH_Y, width: "3.4", height: "6", rx: "1.4", fill: colour, stroke: "none" }), _jsx("rect", { x: x + 4.1, y: GLYPH_Y, width: "3.4", height: "6", rx: "1.4", fill: colour, stroke: "none" })] })));
};
/** Top edge of the control bar. Arrows stop here so they never cross a glyph. */
export var BAR_TOP = 150;
/** The track-menu popup's box, which the arrows must pass underneath. */
export var POPUP = { x: 196, y: 92, width: 116, height: 46 };
/**
 * Where the arrow starts and ends for each target.
 *
 * Routed deliberately, not placed by eye. Everything here shares one shallow
 * corridor between the popup's bottom edge and the top of the control bar:
 * anything steeper would have to cross something, since the popup fills the
 * upper right and the control glyphs fill the bar itself. This is also why the
 * diagram carries no progress bar. One spanning the full width, as the real
 * skin has, leaves no way in from above at all, and it tells the viewer nothing
 * about which button to press.
 *
 * `StepIllustration.test.ts` checks these stay clear, so a nudge here cannot
 * quietly put an arrow back on top of the menu.
 */
export var ARROWS = {
    volume: { from: [108, 128], to: [52, BAR_TOP] },
    audioMenu: { from: [140, 132], to: [AUDIO_X - 9, BAR_TOP] },
    subtitlesMenu: { from: [158, 134], to: [SUBS_X - 9, BAR_TOP] }
};
var StepIllustration = function (_a) {
    var target = _a.target, caption = _a.caption;
    var menu = menuFor(target);
    var audioOn = target === "audioMenu";
    var subsOn = target === "subtitlesMenu";
    var volumeOn = target === "volume";
    var arrow = ARROWS[target];
    return (_jsxs("svg", __assign({ className: "player-diagnostics__step-shot", viewBox: "0 0 320 180", role: "img", "aria-label": caption, preserveAspectRatio: "xMidYMid meet" }, { children: [_jsx("rect", { x: "0", y: "0", width: "320", height: "180", rx: "6", fill: FRAME }), _jsx("rect", { x: "0", y: "150", width: "320", height: "30", fill: BAR }), _jsx("polygon", { points: "12,159 12,171 22,165", fill: CONTROL }), _jsx("polygon", { points: "34,161 38,161 43,157 43,173 38,169 34,169", fill: volumeOn ? ACCENT : CONTROL }), _jsx("path", { d: "M46 162 q3 3 0 6", stroke: volumeOn ? ACCENT : CONTROL, strokeWidth: "1.5", fill: "none" }), _jsx("rect", { x: "53", y: "164", width: "34", height: "2", rx: "1", fill: "#4a4a4a" }), _jsx("rect", { x: "53", y: "164", width: volumeOn ? 34 : 10, height: "2", rx: "1", fill: volumeOn ? ACCENT : CONTROL }), _jsx("rect", { x: "95", y: "162", width: "26", height: "6", rx: "1", fill: "#5a5a5a" }), _jsx(Headphones, { x: AUDIO_X, colour: audioOn ? ACCENT : CONTROL }), _jsx("rect", { x: SUBS_X - 9, y: "158", width: "18", height: "14", rx: "3", fill: "none", stroke: subsOn ? ACCENT : CONTROL, strokeWidth: subsOn ? 1.6 : 1 }), _jsx("text", __assign({ x: SUBS_X, y: "168.5", textAnchor: "middle", fontSize: "7", fill: subsOn ? ACCENT : CONTROL }, { children: "CC" })), PLAIN_CONTROLS.map(function (x) { return (_jsx("rect", { x: x - 6, y: "159", width: "12", height: "12", rx: "2", fill: CONTROL, opacity: "0.55" }, x)); }), volumeOn && _jsx("rect", { x: "28", y: "154", width: "64", height: "22", rx: "5", fill: "none", stroke: ACCENT, strokeWidth: "1.6" }), (audioOn || subsOn) && (_jsx("rect", { x: (audioOn ? AUDIO_X : SUBS_X) - 13, y: "154", width: "26", height: "22", rx: "5", fill: "none", stroke: ACCENT, strokeWidth: "1.6" })), menu && (_jsxs("g", { children: [_jsx("rect", { x: POPUP.x, y: POPUP.y, width: POPUP.width, height: POPUP.height, rx: "5", fill: "#1d1d1d", stroke: "#3a3a3a" }), menu.map(function (row, index) { return (_jsxs("g", { children: [row.active && _jsx("rect", { x: "200", y: 97 + index * 18, width: "108", height: "16", rx: "3", fill: ACCENT, opacity: "0.22" }), _jsx("text", __assign({ x: "208", y: 108 + index * 18, fontSize: "9", fill: row.active ? ACCENT : "#cfcfcf" }, { children: row.text }))] }, row.text)); })] })), _jsx(Arrow, { from: arrow.from, to: arrow.to })] })));
};
export default StepIllustration;
